import React, { useState, useCallback } from "react";
import { Row, Col, Menu, Modal, Result } from "antd";
import { useIntl } from "react-intl";

import {
  post,
  checkResponse,
  checkHttpStatus,
} from "../../../modules/httpFunctions";
import ExternalLink from "../../app/generic/externalLink";
import { handleError } from "../../../modules/errorLogger";
import { deleteEntity } from "../../../modules/crudOperations";
import InformationModal from "../../app/generic/informationModal";

const { Item } = Menu;
const { confirm } = Modal;

function ConsignmentRowMenu({
  onEdit,
  disabled,
  sentList,
  consignment,
  consignmentId,
  loadConsignments,
}) {
  const intl = useIntl();
  const [title, setTitle] = useState();
  const [result, setResult] = useState();
  const [loading, setLoading] = useState();
  const [visible, setVisible] = useState();
  const [loadingMessage, setLoadingMessage] = useState();

  const handleModalClose = useCallback(() => {
    if (result && result.success && !sentList) {
      loadConsignments();
    } else {
      setResult();
    }
  }, [result, sentList, loadConsignments]);

  const createConsignment = useCallback(
    (curTitle, curLoadingMessage, endpoint, message, error) => {
      setLoading(true);
      setVisible(true);
      setTitle(curTitle);
      setLoadingMessage(curLoadingMessage);
      post(`consignment/${consignmentId}/${endpoint}`, undefined)
        .then((r) => {
          setLoading(false);
          if (checkHttpStatus(r) && checkResponse(r)) {
            setResult({
              success: r.data.success,
              message: `${message}. ${
                sentList
                  ? intl.formatMessage({
                      id: "consignment.list.findInUnsend",
                      defaultMessage: "Naleznete ji v neodeslaných zásilkách.",
                    })
                  : ""
              }`,
            });
          } else {
            setResult({
              message: (r.data && r.data.message) || error,
            });
          }
        })
        .catch((err) => {
          setLoading(false);
          setResult({ message: error });
          handleError(err);
        });
    },
    [intl, sentList, consignmentId]
  );

  const handleEditClick = useCallback(
    () => onEdit(consignmentId),
    [onEdit, consignmentId]
  );

  const handleCopyConsignment = useCallback(() => {
    createConsignment(
      intl.formatMessage({
        id: "consignment.copy.copy",
        defaultMessage: "Kopírování zásilky",
      }),
      intl.formatMessage({
        id: "consignment.copy.copying",
        defaultMessage: "Probíhá kopírování zásilky..",
      }),
      "copy",
      intl.formatMessage({
        id: "consignment.copy.success",
        defaultMessage: "Zásilka byla úspěšně zkopírována",
      }),
      intl.formatMessage({
        id: "consignment.copy.error",
        defaultMessage: "Při kopírování nastala chyba!",
      })
    );
  }, [intl, createConsignment]);

  const handleReturnConsignment = useCallback(() => {
    createConsignment(
      intl.formatMessage({
        id: "consignment.return.create",
        defaultMessage: "Vratná zásilka",
      }),
      intl.formatMessage({
        id: "consignment.return.creating",
        defaultMessage: "Probíhá vytváření vratné zásilky..",
      }),
      "return-consignment",
      intl.formatMessage({
        id: "consignment.return.success",
        defaultMessage: "Vratná zásilka byla úspěšně vytvořena",
      }),
      intl.formatMessage({
        id: "consignment.return.error",
        defaultMessage: "Při vytváření nastala chyba!",
      })
    );
  }, [intl, createConsignment]);

  const dropConsignment = useCallback(
    (cancel) => {
      deleteEntity(
        "consignment",
        consignmentId,
        intl.formatMessage({
          id: "consignment.unsent",
          defaultMessage: "Neodeslaná zásilka",
        }),
        intl.formatMessage({
          id: "consignment.errorName",
          defaultMessage: "zásilky",
        }),
        loadConsignments,
        true,
        "",
        cancel
          ? intl.formatMessage({
              id: "consignment.cancel.success",
              defaultMessage: "Zásilka byla úspěšně stornována.",
            })
          : ""
      );
    },
    [intl, consignmentId, loadConsignments]
  );

  const handleDeleteClick = useCallback(
    (cancel) => {
      confirm({
        title: intl.formatMessage({
          id: "messages.confirm",
          defaultMessage: "Potvrzení",
        }),
        content: cancel
          ? intl.formatMessage({
              id: "consignment.cancel.question",
              defaultMessage: "Opravdu si přejete tuto zásilku stornovat?",
            })
          : intl.formatMessage({
              id: "messages.drop.question",
              defaultMessage: "Opravdu si přejete odstranit tento záznam?",
            }),
        okText: intl.formatMessage({
          id: "messages.yes",
          defaultMessage: "Ano",
        }),
        cancelText: intl.formatMessage({
          id: "messages.no",
          defaultMessage: "Ne",
        }),
        onOk: () => dropConsignment(cancel),
      });
    },
    [intl, dropConsignment]
  );

  return (
    <Row>
      <Col>
        <InformationModal
          title={title}
          loading={loading}
          visible={visible}
          setVisible={setVisible}
          setLoading={setLoading}
          setResult={handleModalClose}
          loadingMessage={loadingMessage}
        >
          <Result
            title={result && result.message}
            status={result && result.success ? "success" : "error"}
          />
        </InformationModal>
        <Menu className="dropdown-menu">
          <Item key={1} disabled={disabled} onClick={handleEditClick}>
            {sentList
              ? intl.formatMessage({
                  id: "consignment.detail",
                  defaultMessage: "Detail zásilky",
                })
              : intl.formatMessage({
                  id: "consignment.edit",
                  defaultMessage: "Editace zásilky",
                })}
          </Item>
          <Item key={2} disabled={disabled} onClick={handleCopyConsignment}>
            {intl.formatMessage({
              id: "consignment.copy",
              defaultMessage: "Kopie zásilky",
            })}
          </Item>
          {sentList && (
            <Item
              key={3}
              title={
                consignment.cancelled
                  ? intl.formatMessage({
                      id: "consignment.cancel.done",
                      defaultMessage: "Zásilka již byla stornována",
                    })
                  : consignment.canBeCancelled
                  ? intl.formatMessage({
                      id: "consignment.cancel.info",
                      defaultMessage:
                        "Odešle požadavek na stornování zásilky do DHL",
                    })
                  : intl.formatMessage({
                      id: "consignment.cancel.info.not",
                      defaultMessage: "Zásilku již není možné stornovat",
                    })
              }
              onClick={() => handleDeleteClick(true)}
              disabled={
                disabled || consignment.cancelled || !consignment.canBeCancelled
              }
            >
              {intl.formatMessage({
                id: "consignment.cancel",
                defaultMessage: "Stornovat zásilku",
              })}
            </Item>
          )}
          {sentList ? (
            <Item key={4} disabled={disabled} onClick={handleReturnConsignment}>
              {intl.formatMessage({
                id: "consignment.return",
                defaultMessage: "Vytvořit vratnou zásilku",
              })}
            </Item>
          ) : (
            <Item key={5} disabled={disabled} onClick={handleDeleteClick}>
              {intl.formatMessage({
                id: "messages.delete",
                defaultMessage: "Smazat",
              })}
            </Item>
          )}
          {sentList && (
            <Item
              key={6}
              title={
                consignment.proofOfDeliveryUrl
                  ? intl.formatMessage({
                      id: "consignment.pod.show",
                      defaultMessage: "Kliknutím zobrazíte POD zásilky",
                    })
                  : intl.formatMessage({
                      id: "consignment.pod.empty",
                      defaultMessage: "POD není doposud dostupné",
                    })
              }
              disabled={disabled || !consignment.proofOfDeliveryUrl}
            >
              <ExternalLink
                id="consignment.pod"
                defaultMessage="POD zásilky"
                href={consignment.proofOfDeliveryUrl}
              />
            </Item>
          )}
        </Menu>
      </Col>
    </Row>
  );
}

export default ConsignmentRowMenu;
